import React, { useState, useContext } from "react";
import Footer from "./Footer";
import "../css/shop.scss";
import { useNavigate } from "react-router-dom";
import productData from "../Datas/productData";
import Header from "./Header";
import { CartContext } from "./CartContext";

function Shop() {
  const [activeFilter, setActiveFilter] = useState("All");
  const [sortBy, setSortBy] = useState("featured");
  const [currentPage, setCurrentPage] = useState(1);
  const { addToCart } = useContext(CartContext);
  const navigate = useNavigate();

  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const categories = [
    "All",
    "Toys",
    "Kennels",
    "Bowls",
    "Food Storage",
    "Litter",
    "Scratchers",
    "Waterers",
    "Beds",
    "Accessories",
  ];

  const filteredProducts =
    activeFilter === "All"
      ? productData
      : productData.filter((product) => product.category === activeFilter);

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case "price-low":
        return parseFloat(a.price) - parseFloat(b.price);
      case "price-high":
        return parseFloat(b.price) - parseFloat(a.price);
      case "rating":
        return parseFloat(b.rating) - parseFloat(a.rating);
      case "name":
        return a.name.localeCompare(b.name);
      default:
        return a.id - b.id;
    }
  });

  const productsPerPage = 12;
  const totalPages = Math.ceil(sortedProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const currentProducts = sortedProducts.slice(
    startIndex,
    startIndex + productsPerPage,
  );

  const handleFilterClick = (category) => {
    setActiveFilter(category);
    setCurrentPage(1);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setCurrentPage(1);
  };

  const handleProductClick = (productId) => {
    navigate(`/shopDetails/${productId}`);
    toTop();
  };

  const handleQuickAdd = (e, product) => {
    e.stopPropagation();
    addToCart(product.id, 1, product.size[0], product.colors[0]);
    navigate("/cartpage");
    toTop();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    toTop();
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <svg key={i} className="star" viewBox="0 0 24 24">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>,
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <svg key={i} className="star" viewBox="0 0 24 24">
            <defs>
              <linearGradient id="halfStar">
                <stop offset="50%" stopColor="#f5a623" />
                <stop offset="50%" stopColor="#ddd" />
              </linearGradient>
            </defs>
            <polygon
              points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
              fill="url(#halfStar)"
            />
          </svg>,
        );
      } else {
        stars.push(
          <svg key={i} className="star empty" viewBox="0 0 24 24">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>,
        );
      }
    }
    return stars;
  };

  return (
    <div className="shop main">
      <Header />

      <section className="shop__hero">
        <div className="shop__hero-content">
          <h1>Pet Shop</h1>
          <p>Premium pet products for your beloved companions</p>
        </div>
      </section>

      <div className="shop__controls">
        <div className="shop__filters">
          {categories.map((category) => (
            <button
              key={category}
              className={`filter-btn ${
                activeFilter === category ? "active" : ""
              }`}
              onClick={() => handleFilterClick(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="shop__sort">
          <label>Sort by:</label>
          <select value={sortBy} onChange={handleSortChange}>
            <option value="featured">Featured</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="rating">Highest Rated</option>
            <option value="name">Name: A to Z</option>
          </select>
        </div>
      </div>

      <p className="shop__count">
        Showing {startIndex + 1}-
        {Math.min(startIndex + productsPerPage, sortedProducts.length)} of{" "}
        {sortedProducts.length} products
      </p>

      <div className="shop__grid">
        {currentProducts.map((product) => (
          <article
            key={product.id}
            className="product-card"
            onClick={() => handleProductClick(product.id)}
          >
            <div className="product-card__image-wrap">
              <img
                className="product-card__image"
                src={product.image}
                alt={product.name}
                loading="lazy"
              />
              <button
                className="product-card__quick-add"
                onClick={(e) => handleQuickAdd(e, product)}
              >
                Quick Add
              </button>
              <span className="product-card__badge">New</span>
              <button
                className="product-card__wishlist"
                onClick={(e) => e.stopPropagation()}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </button>
            </div>

            <div className="product-card__info">
              <p className="product-card__category">{product.category}</p>
              <h3 className="product-card__name">{product.name}</h3>

              <div className="product-card__meta">
                <span className="product-card__price">
                  HK${Number(product.price).toLocaleString("en-US")}
                </span>
                <div className="product-card__rating">
                  <div className="product-card__stars">
                    {renderStars(parseFloat(product.rating))}
                  </div>
                  <span className="product-card__rating-text">
                    ({product.reviews})
                  </span>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="shop__pagination">
          <button
            className="page-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <svg viewBox="0 0 24 24">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`page-btn ${currentPage === page ? "active" : ""}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}

          <button
            className="page-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <svg viewBox="0 0 24 24">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default Shop;
